import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Sparkles } from "lucide-react";
import { uploadQuoteFile } from "@/lib/crmApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QuoteLinesEditor, EMPTY_LINE } from "@/components/crm/QuoteLinesEditor";

const inputCls = "mt-1 w-full h-11 px-3 rounded-xl border border-slate-300 text-sm outline-none focus:border-slate-500 bg-white";
const Field = ({ label, className = "", children }) => (
  <label className={`block ${className}`}><span className="text-xs font-medium text-slate-500">{label}</span>{children}</label>
);
const num = (v) => (v === "" || v == null ? null : parseFloat(v));
const str = (v) => (v == null ? "" : String(v));

export const QuoteDialog = ({ leadId, meta, onClose, onSave, saving }) => {
  const [q, setQ] = useState({ quote_no: "", date_sent: "", product: "", price: "", currency: "INR", unit: "per kg", remarks: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [moq, setMoq] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ai, setAi] = useState(false);
  const set = (k) => (e) => setQ((x) => ({ ...x, [k]: e.target.value }));

  const pick = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadQuoteFile(leadId, f);
      setFile(r);
      const ex = r.extracted || {};
      const exLines = (ex.lines || []).map((l) => ({ description: str(l.description), grade: str(l.grade), thickness_mm: str(l.thickness_mm), qty_kg: str(l.qty_kg), rate_per_kg: str(l.rate_per_kg) }));
      if (exLines.length) setLines(exLines);
      if (ex.moq_kg) setMoq(str(ex.moq_kg));
      setQ((x) => ({
        ...x,
        date_sent: ex.date || x.date_sent,
        quote_no: ex.quote_no || x.quote_no,
        currency: ex.currency || x.currency,
        product: x.product || [...new Set(exLines.map((l) => [l.description, l.grade, l.thickness_mm && `${l.thickness_mm} mm`].filter(Boolean).join(", ")))].join(" · "),
        price: x.price || str(exLines[0]?.rate_per_kg),
        unit: exLines.length ? "per kg" : x.unit,
      }));
      setAi(!!(ex.date || exLines.length));
      if (!ex.date && !exLines.length) toast.message("Couldn't read details from the file — please fill them in.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = () => onSave({
    ...q, price: num(q.price), moq_kg: num(moq), file_id: file?.file_id || null,
    lines: lines.map((l) => ({ description: l.description, grade: l.grade, thickness_mm: num(l.thickness_mm), qty_kg: num(l.qty_kg), rate_per_kg: num(l.rate_per_kg) })),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quote-dialog" className="rounded-2xl sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-xl">Add quote</DialogTitle></DialogHeader>
        <label data-testid="quote-file-drop" className="flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-500 px-4 py-4 cursor-pointer transition-colors">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Upload className="h-5 w-5 text-slate-400" />}
          <span className="text-sm text-slate-600 flex-1 truncate">
            {uploading ? "Uploading & reading the quote…" : file ? <span data-testid="quote-file-name" className="font-medium text-slate-900">{file.filename}</span> : "Upload quote (PDF, JPG or PNG) — details are filled in for you"}
          </span>
          {ai && <span data-testid="quote-date-ai" className="inline-flex items-center gap-1 text-xs text-violet-600"><Sparkles className="h-3.5 w-3.5" /> Auto-filled</span>}
          <input data-testid="quote-file-input" type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Date sent"><input data-testid="quote-date" type="date" value={q.date_sent} onChange={set("date_sent")} className={inputCls} /></Field>
          <Field label="Quote no."><input data-testid="quote-no" value={q.quote_no} onChange={set("quote_no")} className={inputCls} /></Field>
          <Field label="Currency">
            <select data-testid="quote-currency" value={q.currency} onChange={set("currency")} className={inputCls}>{meta.currencies.map((c) => <option key={c}>{c}</option>)}</select>
          </Field>
          <Field label="Unit">
            <select data-testid="quote-unit" value={q.unit} onChange={set("unit")} className={inputCls}>{meta.units.map((u) => <option key={u}>{u}</option>)}</select>
          </Field>
          <Field label="Product" className="col-span-2 sm:col-span-3"><input data-testid="quote-product" value={q.product} onChange={set("product")} placeholder="EI-66, CRNGO 50C470, 0.50 mm" className={inputCls} /></Field>
          <Field label="Price"><input data-testid="quote-price" type="number" step="any" value={q.price} onChange={set("price")} className={inputCls} /></Field>
        </div>
        <QuoteLinesEditor lines={lines} setLines={setLines} moq={moq} setMoq={setMoq} currency={q.currency} />
        <Field label="Remarks"><input data-testid="quote-remarks" value={q.remarks} onChange={set("remarks")} className={inputCls} /></Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="h-11 rounded-xl">Cancel</Button>
          <Button data-testid="quote-save" disabled={saving || uploading} onClick={submit} className="h-11 px-6 rounded-xl bg-slate-900 hover:bg-slate-800">
            {saving ? "Saving…" : "Save quote"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
